import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import { Button, IconButton, Stack, TextField } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import CloseIcon from "@mui/icons-material/Close";
import { T } from "../../../lib/types/common";
import { Messages } from "../../../lib/config";
import { LoginInput, MemberInput } from "../../../lib/types/member";
import MemberService from "../../services/MemberService";
import { sweetErrorHandling } from "../../../lib/sweetAlert";
import { useGlobals } from "../../hooks/useGlobals";
import "../../../css/auth.css";

const useStyles = makeStyles(() => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

interface AuthenticationModalProps {
  signupOpen: boolean;
  loginOpen: boolean;
  setSignupOpen: (isOpen: boolean) => void;
  setLoginOpen: (isOpen: boolean) => void;
  handleSignupClose: () => void;
  handleLoginClose: () => void;
}

export default function AuthenticationModal(props: AuthenticationModalProps) {
  const {
    signupOpen,
    loginOpen,
    setSignupOpen,
    setLoginOpen,
    handleSignupClose,
    handleLoginClose,
  } = props;
  const classes = useStyles();
  const [memberNick, setMemberNick] = useState<string>("");
  const [memberPhone, setmemberPhone] = useState<string>("");
  const [memberPassword, setMemberPassword] = useState<string>("");
  const { setAuthMember } = useGlobals();
  const isSignupReady =
    memberNick.trim() !== "" &&
    memberPhone.trim() !== "" &&
    memberPassword.trim() !== "";
  const isLoginReady = memberNick.trim() !== "" && memberPassword.trim() !== "";

  /** HANDLERS **/

  const handleUserName = (e: T) => {
    setMemberNick(e.target.value);
  };
  const handlePhone = (e: T) => {
    setmemberPhone(e.target.value);
  };
  const handlePassword = (e: T) => {
    setMemberPassword(e.target.value);
  };

  const handlePasswordKeyDown = (e: T) => {
    if (e.key === "Enter" && signupOpen && isSignupReady) {
      handleSingupRequest().then();
    } else {
      if (e.key === "Enter" && loginOpen && isLoginReady) {
        handleLoginRequest().then();
      }
    }
  };

  const openLoginModal = () => {
    setSignupOpen(false);
    setLoginOpen(true);
  };

  const openSignupModal = () => {
    setLoginOpen(false);
    setSignupOpen(true);
  };

  // handleSingupRequest
  const handleSingupRequest = async () => {
    try {
      console.log("inputs:", memberNick, memberPhone, memberPassword);
      if (!isSignupReady) throw new Error(Messages.error3);

      const signupInput: MemberInput = {
        memberNick: memberNick,
        memberPhone: memberPhone,
        memberPassword: memberPassword,
      };

      const member = new MemberService();
      const result = await member.signup(signupInput);

      //saving Authinticated User
      setAuthMember(result);
      handleSignupClose();
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  // handleLoginRequest
  const handleLoginRequest = async () => {
    try {
      if (!isLoginReady) throw new Error(Messages.error3);

      const loginInput: LoginInput = {
        memberNick: memberNick,
        memberPassword: memberPassword,
      };

      const member = new MemberService();
      const result = await member.login(loginInput);
      // handleLoginRequest
      setAuthMember(result);
      handleLoginClose();
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={signupOpen}
        onClose={handleSignupClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={signupOpen}>
          <Stack className={"auth-modal-card"} direction={"row"}>
            <IconButton className={"auth-close"} onClick={handleSignupClose}>
              <CloseIcon />
            </IconButton>
            <Stack className={"auth-visual"}>
              <img src={"/img/auth.webp"} alt="" />
              <span>Booksaw account</span>
              <h3>Join readers who collect better books.</h3>
            </Stack>
            <Stack className={"auth-form-panel"}>
              <span className={"auth-kicker"}>Create account</span>
              <h2>Start your reading shelf.</h2>
              <p>Save books, build your cart, and track your orders.</p>
              <TextField
                className={"auth-input"}
                id="signup-username"
                label="username"
                variant="outlined"
                onChange={handleUserName}
              />
              <TextField
                className={"auth-input"}
                id="signup-phone"
                label="phone number"
                variant="outlined"
                onChange={handlePhone}
              />
              <TextField
                className={"auth-input"}
                id="signup-password"
                label="password"
                variant="outlined"
                type={"password"}
                onChange={handlePassword}
                onKeyDown={handlePasswordKeyDown}
              />
              <Button
                className={"auth-submit"}
                onClick={handleSingupRequest}
                startIcon={<LoginIcon />}
                disabled={!isSignupReady}
              >
                Create account
              </Button>
              <div className={"auth-switch"}>
                Already have an account?
                <button type="button" onClick={openLoginModal}>
                  Login
                </button>
              </div>
            </Stack>
          </Stack>
        </Fade>
      </Modal>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={loginOpen}
        onClose={handleLoginClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={loginOpen}>
          <Stack className={"auth-modal-card login"} direction={"row"}>
            <IconButton className={"auth-close"} onClick={handleLoginClose}>
              <CloseIcon />
            </IconButton>
            <Stack className={"auth-visual"}>
              <img src={"/img/auth.webp"} alt="" />
              <span>Welcome back</span>
              <h3>Your next favorite book is waiting.</h3>
            </Stack>
            <Stack className={"auth-form-panel"}>
              <span className={"auth-kicker"}>Member login</span>
              <h2>Welcome back.</h2>
              <p>Log in to continue shopping and manage your orders.</p>
              <TextField
                className={"auth-input"}
                id="login-username"
                label="username"
                variant="outlined"
                onChange={handleUserName}
              />
              <TextField
                className={"auth-input"}
                id={"login-password"}
                label={"password"}
                variant={"outlined"}
                type={"password"}
                onChange={handlePassword}
                onKeyDown={handlePasswordKeyDown}
              />
              <Button
                className={"auth-submit"}
                onClick={handleLoginRequest}
                startIcon={<LoginIcon />}
                disabled={!isLoginReady}
              >
                Login
              </Button>
              <div className={"auth-switch"}>
                Don&apos;t have an account?
                <button type="button" onClick={openSignupModal}>
                  Sign up
                </button>
              </div>
            </Stack>
          </Stack>
        </Fade>
      </Modal>
    </div>
  );
}
